/** v3 API helpers for the durable streaming architecture (POST /v3/messages, SSE, cancel). */
import type { paths } from '@luxuryescapes/contract-trip'
import request from 'api/requestUtils'
import config from 'constants/config'
import { getOptions, baseUri } from './utils'
import { openSseConnection } from '../utils/sseClient'
import type { CreateMessageRequest, CreateMessageResponse } from './types'

function absoluteUrl(path: string): string {
  const isSSR = typeof window === 'undefined'
  const apiHost = isSSR ? (config.SERVER_SIDE_API_HOST ?? config.API_HOST) : config.API_HOST
  const endpoint = apiHost.startsWith('http') ? apiHost : `${config.SCHEME}://${apiHost}`
  return `${endpoint}${path}`
}

type SubmitMessageParams = CreateMessageRequest
type SubmitMessageResult = CreateMessageResponse

type RenameChatOperation = paths['/api/trips/v3/chats/{chatId}']['patch']
type RenameChatRequest = RenameChatOperation['requestBody']['content']['application/json']
export type RenameChatResult = RenameChatOperation['responses']['200']['content']['application/json']['result']
type CancelMessageOperation = paths['/api/trips/v3/chats/{chatId}/cancel']['post']
type CancelMessageResult = CancelMessageOperation['responses']['200']['content']['application/json']['result']

export class ChatBusyError extends Error {
  inFlightMessageId: string
  constructor(inFlightMessageId: string) {
    super('Chat is busy — a response is already being generated')
    this.inFlightMessageId = inFlightMessageId
  }
}

export class StreamingUnavailableError extends Error {
  constructor() {
    super('Chat is temporarily unavailable. Please try again.')
  }
}

function isApiErrorObject(error: unknown): error is { status?: number; error?: string; inFlightMessageId?: string; currentStatus?: string } {
  return error !== null && typeof error === 'object'
}

interface SseStreamHandlers {
  onEvent: (event: string, data: string, id: string) => void
  onOpen?: () => void
  onError?: (err: Error) => void
  onFatalError?: (err: Error) => void
}

export async function renameChat(
  params: { chatId: number } & RenameChatRequest,
  accessToken?: string,
): Promise<RenameChatResult> {
  const uri = `${baseUri}/v3/chats/${params.chatId}`
  const response = await request
    .patch<App.ApiResponse<RenameChatResult>, RenameChatRequest>(
      uri,
      { name: params.name },
      { ...getOptions(accessToken) },
    )
  return response.result
}

/**
 * POST /v3/messages — submit a message and start a detached generation.
 * Returns 202 with assistantMessageId and streamUrl for SSE subscription.
 * Throws ChatBusyError on 409 or StreamingUnavailableError on 503.
 */
export async function submitMessage(
  params: SubmitMessageParams,
  accessToken?: string,
): Promise<SubmitMessageResult> {
  const uri = `${baseUri}/v3/messages`
  try {
    const response = await request
      .post<App.ApiResponse<SubmitMessageResult>, SubmitMessageParams>(uri, params, { ...getOptions(accessToken) })
    return response.result
  } catch(err: unknown) {
    if (isApiErrorObject(err) && err.status === 409 && err.error === 'chat_busy') {
      throw new ChatBusyError(String(err.inFlightMessageId ?? ''))
    }

    if (isApiErrorObject(err) && err.status === 503) {
      throw new StreamingUnavailableError()
    }

    if (err instanceof Response) {
      if (err.status === 409) {
        const body = await err.json().catch(() => ({}))
        if (body.error === 'chat_busy') {
          throw new ChatBusyError(String(body.inFlightMessageId ?? ''))
        }
      }

      if (err.status === 503) {
        throw new StreamingUnavailableError()
      }
    }

    throw err
  }
}

/**
 * GET /v3/chats/:chatId/stream — subscribe to generation events via SSE.
 * Returns a connection handle with a close() method.
 */
export function subscribeChatStream(
  chatId: number,
  cursor: string,
  accessToken: string | undefined,
  handlers: SseStreamHandlers,
  signal?: AbortSignal,
): { close: () => void } {
  const url = absoluteUrl(`${baseUri}/v3/chats/${chatId}/stream`)

  return openSseConnection({
    url,
    accessToken,
    cursor,
    signal,
    handlers,
  })
}

/**
 * POST /v3/chats/:chatId/cancel — cancel an in-flight generation.
 * Returns 200 with CANCELLED status. Treats 409 cancel_already_terminal with
 * currentStatus=CANCELLED as success (idempotent cancel).
 */
export async function cancelMessage(
  chatId: number,
  accessToken?: string,
): Promise<CancelMessageResult> {
  const uri = `${baseUri}/v3/chats/${chatId}/cancel`
  try {
    const response = await request
      .post<App.ApiResponse<CancelMessageResult>, Record<string, never>>(uri, {}, { ...getOptions(accessToken) })
    return response.result
  } catch(err: unknown) {
    if (isApiErrorObject(err) && err.status === 409 && err.error === 'cancel_already_terminal' && err.currentStatus === 'CANCELLED') {
      return { status: 'CANCELLED', messageId: '' }
    }

    if (err instanceof Response && err.status === 409) {
      const body = await err.json().catch(() => ({}))
      if (body.error === 'cancel_already_terminal' && body.currentStatus === 'CANCELLED') {
        return { status: 'CANCELLED', messageId: '' }
      }
    }

    throw err
  }
}
